import { useId } from 'react';
import { cn } from '@helpers/cn';
import type { PercentRect } from './geometry';

export type BoundingBoxVariant = 'highlight' | 'outline';

interface BoundingBoxOverlayProps {
  /** Boxes to draw, positioned as percentages of the overlay's container. */
  rects: PercentRect[];
  /**
   * - `highlight`: yellow outline + darkened backdrop with cut-outs. Used to
   *   point out several regions on a full screenshot.
   * - `outline`: a single thin red outline. Used on a tightly cropped preview.
   */
  variant?: BoundingBoxVariant;
}

const toStyle = ({ left, top, width, height }: PercentRect) => ({
  left: `${left}%`,
  top: `${top}%`,
  width: `${width}%`,
  height: `${height}%`,
});

/**
 * Darkens everything except the given rectangles by punching holes into a
 * semi-transparent overlay with an SVG mask. Each instance gets a unique mask
 * id so multiple overlays can coexist on one page.
 */
const HighlightBackdrop = ({ rects }: { rects: PercentRect[] }) => {
  const maskId = useId();

  return (
    <svg
      className='eye:absolute eye:inset-0 eye:h-full eye:w-full'
      xmlns='http://www.w3.org/2000/svg'
    >
      <defs>
        <mask id={maskId}>
          <rect fill='white' x='0' y='0' width='100%' height='100%' />
          {rects.map((rect, index) => (
            <rect
              key={index}
              fill='black'
              x={`${rect.left}%`}
              y={`${rect.top}%`}
              width={`${rect.width}%`}
              height={`${rect.height}%`}
            />
          ))}
        </mask>
      </defs>
      <rect
        x='0'
        y='0'
        width='100%'
        height='100%'
        fill='rgba(0,0,0,0.33)'
        mask={`url(#${maskId})`}
      />
    </svg>
  );
};

/**
 * Renders bounding boxes over a `position: relative` container. Purely
 * presentational: callers supply already-normalised {@link PercentRect}s.
 */
const BoundingBoxOverlay = ({
  rects,
  variant = 'highlight',
}: BoundingBoxOverlayProps) => {
  if (!rects.length) return null;

  const boxClassName =
    variant === 'highlight'
      ? 'eye:outline-3 eye:outline-solid eye:outline-[#ffcf00] eye:hover:outline-4 eye:hover:outline-[#ff8100] eye:hover:bg-black/10'
      : 'eye:outline-2 eye:outline-solid eye:outline-[red]';

  return (
    <>
      {variant === 'highlight' && <HighlightBackdrop rects={rects} />}

      {rects.map((rect, index) => (
        <div
          key={index}
          style={toStyle(rect)}
          className={cn('eye:absolute eye:box-border', boxClassName)}
        />
      ))}
    </>
  );
};

export default BoundingBoxOverlay;
